import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { parseLocalDate } from "@/lib/validation";

export interface AuditLogEntry {
  id: string;
  user_id: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  metadata: Record<string, any>;
  ip_address: string | null;
  created_at: string;
}

interface AuditFilters {
  action?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export function useAuditLog() {
  const { user } = useAuth();

  const logAction = useCallback(
    async (
      action: string,
      resourceType?: string,
      resourceId?: string,
      metadata?: Record<string, any>
    ) => {
      if (!user) return;
      try {
        await (supabase as any).rpc("log_audit_event", {
          _action: action,
          _resource_type: resourceType ?? null,
          _resource_id: resourceId ?? null,
          _metadata: metadata ?? {},
        });
      } catch {
        // silent — audit logging should never break UX
      }
    },
    [user]
  );

  return { logAction };
}

export function useAuditLogEntries(filters: AuditFilters) {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("audit_log")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false });

    if (filters.action) query = query.eq("action", filters.action);
    // Bare "yyyy-MM-dd" strings get parsed by Postgres in the session's (UTC)
    // timezone, not the caller's — shifting the boundary by the caller's UTC
    // offset. Compute the actual local-day-boundary instants client-side instead.
    if (filters.startDate) {
      const start = parseLocalDate(filters.startDate);
      query = query.gte("created_at", start.toISOString());
    }
    if (filters.endDate) {
      const end = parseLocalDate(filters.endDate);
      end.setHours(23, 59, 59, 999);
      query = query.lte("created_at", end.toISOString());
    }
    if (filters.search) {
      query = query.or(
        `action.ilike.%${filters.search}%,resource_type.ilike.%${filters.search}%`
      );
    }
    query = query.range(
      filters.offset ?? 0,
      (filters.offset ?? 0) + (filters.limit ?? 25) - 1
    );

    const { data, error, count } = await query;
    setLoading(false);
    if (!error && data) {
      setEntries(data as unknown as AuditLogEntry[]);
      setTotal(count ?? 0);
    }
  }, [filters.action, filters.startDate, filters.endDate, filters.search, filters.limit, filters.offset]);

  return { entries, total, loading, fetchEntries };
}
